import React, { useState, useEffect } from 'react';
import { useNotification } from '../../context/CustomDialogContext';
import { useNavigate } from 'react-router-dom';
import { forgotPassword, verifyResetOtp, resetPassword } from '../../services/authService';
import ErrorMessage from '../../components/ErrorMessage';

const ForgotPassword = () => {
  const { showAlert, showConfirm } = useNotification();
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: Reset Password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.body.className = 'login-body';
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setLoading(true);
    try {
      const res = await forgotPassword(email);
      setSuccessMsg('If this email is registered, an OTP has been sent.');
      setStep(2);
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setLoading(true);
    try {
      await verifyResetOtp(email, otp);
      setSuccessMsg('OTP verified successfully. Please set your new password.');
      setStep(3);
    } catch (err) {
      setError(err.message || 'Invalid or expired OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setLoading(true);
    try {
      await resetPassword(email, otp, newPassword, confirmPassword);
      await showAlert('Success', 'Password updated successfully. Please login with your new password.', 'success');
      navigate('/login');
    } catch (err) {
      setError(err.message || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-body" style={{ display: 'flex', minHeight: '100vh', justifyContent: 'center', alignItems: 'center', backgroundColor: 'var(--bg-base)' }}>
      <div className="login-card-wrapper" style={{ width: '100%', maxWidth: '450px', padding: '20px' }}>
        <div className="login-card" style={{ padding: '32px' }}>
          
          <div className="login-logo" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style={{ width: '32px', height: '32px', fill: 'var(--primary)' }}>
              <path d="M12 21.5c.3 0 .5-.2.5-.5V12c0-.3-.2-.5-.5-.5s-.5.2-.5.5v9c0 .3.2.5.5.5z"/>
              <path d="M12 2C8.3 2 4.4 5.9 3.5 9c-.5 1.7-.1 3.4 1.1 4.5.9.8 2.1 1.2 3.4 1.2.7 0 1.5-.1 2.2-.4.6-.3 1.2-.7 1.8-1.2.6.5 1.2.9 1.8 1.2.7.3 1.5.4 2.2.4 1.3 0 2.5-.4 3.4-1.2 1.2-1.1 1.6-2.8 1.1-4.5C19.6 5.9 15.7 2 12 2zm6 10.3c-.6.6-1.5.9-2.4.9-.5 0-1-.1-1.4-.3-.6-.3-1.1-.7-1.6-1.2-.5-.5-.8-1.1-.9-1.8-.1-.4 0-.8.2-1.1.2-.4.6-.7 1-.8.9-.2 1.8.2 2.3 1 .4.6.6 1.4.6 2.1l2.2.2zm-9.8.9c-.9 0-1.8-.3-2.4-.9l2.2-.2c0-.7.2-1.5.6-2.1.5-.8 1.4-1.2 2.3-1 .4.1.8.4 1 .8.2.3.3.7.2 1.1-.1.7-.4 1.3-.9 1.8-.4.2-.9.3-1.4.3z"/>
            </svg>
            <span style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--primary)' }}>PranaFlow Portal</span>
          </div>

          <h2 className="login-title" style={{ textAlign: 'center', fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '8px' }}>Password Recovery</h2>
          <p className="login-desc" style={{ textAlign: 'center', fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '24px' }}>
            {step === 1 && 'Enter your registered email address to receive an OTP code.'}
            {step === 2 && 'Enter the 6-digit OTP code sent to your email.'}
            {step === 3 && 'Choose a secure new password for your account.'}
          </p>

          {error && <ErrorMessage message={error} />}
          {successMsg && (
            <div className="portal-alert portal-alert-success" style={{ marginBottom: '16px', fontSize: '0.85rem' }}>
              {successMsg}
            </div>
          )}

          {step === 1 && (
            <form onSubmit={handleEmailSubmit}>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="recoveryEmail" className="form-label" style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px' }}>Email Address</label>
                <input
                  type="email"
                  id="recoveryEmail"
                  className="form-input"
                  placeholder="name@example.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{ width: '100%', padding: '12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}
                />
              </div>

              <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '14px', border: 'none', cursor: 'pointer' }} disabled={loading}>
                {loading ? 'Sending OTP...' : 'Send Reset OTP'}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleOtpSubmit}>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="recoveryOtp" className="form-label" style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px' }}>Verification OTP</label>
                <input
                  type="text"
                  id="recoveryOtp"
                  className="form-input"
                  placeholder="Enter 6-digit code"
                  required
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  style={{ width: '100%', padding: '12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', letterSpacing: '4px', textAlign: 'center', fontSize: '1.2rem', fontWeight: 'bold' }}
                />
              </div>

              <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '14px', border: 'none', cursor: 'pointer' }} disabled={loading}>
                {loading ? 'Verifying...' : 'Verify OTP'}
              </button>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handleResetSubmit}>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label htmlFor="newPassword" className="form-label" style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px' }}>New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  className="form-input"
                  placeholder="Min 8 characters"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{ width: '100%', padding: '12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="confirmPassword" className="form-label" style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px' }}>Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  className="form-input"
                  placeholder="Repeat new password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{ width: '100%', padding: '12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}
                />
              </div>

              <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '14px', border: 'none', cursor: 'pointer' }} disabled={loading}>
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </button>
            </form>
          )}

          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <span onClick={() => navigate('/login')} style={{ fontSize: '0.85rem', color: 'var(--text-light)', cursor: 'pointer', textDecoration: 'underline' }}>
              Return to login screen
            </span>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
